import {
  MarketQuotesConfig,
  ProposalArtifactType,
  QuoteComparisonConfig,
} from '@common/types/submission-placement-task-config';
import { getProposalArtifacts } from '@common/utils/placement-task-utils';
import { BoxItem } from 'box-ui-elements/es';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { BoxTemplateLabels, FileType } from 'enums';
import { PartialSubmission } from 'types';
import { getFileMetadataField } from 'utils';
import useLoadFilesSelection from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/components/Files/useLoadFilesSelection';
import { useFetchSubmissionFiles } from 'broker/pages/SubmissionWorkspacePage/hooks/useFetchSubmissionFiles';

interface UseLoadProposalFilesProps {
  submission: PartialSubmission;
  isEmptyState: boolean;
  setIsDirty: (dirty: boolean) => void;
}

function isFileOfType(file: BoxItem, filesTypes: FileType[]) {
  return filesTypes.includes(getFileMetadataField(file, BoxTemplateLabels.FileType));
}

function getLatestFileOfType(files: BoxItem[], fileTypes: FileType[]): string | undefined {
  return files
    .filter((file) => isFileOfType(file, fileTypes))
    .sort((a, b) => moment(b.modified_at).diff(moment(a.modified_at)))[0]?.id;
}

function getAllFilesOfType(files: BoxItem[], fileTypes: FileType[]): string[] {
  return files.filter((file) => isFileOfType(file, fileTypes)).map((file) => file.id);
}

export default function useLoadProposalFiles({ submission, isEmptyState, setIsDirty }: UseLoadProposalFilesProps) {
  const { items: files } = useFetchSubmissionFiles();

  let proposalArtifacts = getProposalArtifacts(submission);

  // Fallback to legacy behavior
  if (proposalArtifacts === undefined) {
    proposalArtifacts = [
      { type: ProposalArtifactType.MarketQuotes } as MarketQuotesConfig,
      { type: ProposalArtifactType.QuoteComparison } as QuoteComparisonConfig,
    ];
  }

  const shouldAutoAttachFiles = isEmptyState && !isEmpty(proposalArtifacts);

  const autoSelectedFileIds = useMemo(() => {
    if (!shouldAutoAttachFiles) {
      return [];
    }

    const selectedFiles: string[] = [];

    proposalArtifacts?.forEach((artifact) => {
      switch (artifact.type) {
        case ProposalArtifactType.MarketQuotes: {
          selectedFiles.push(...getAllFilesOfType(files, [FileType.MarketQuote, FileType.RenewalPolicy]));
          break;
        }

        case ProposalArtifactType.QuoteComparison: {
          const latestComparisonFile = getLatestFileOfType(files, [FileType.QuoteComparison]);
          if (latestComparisonFile) {
            selectedFiles.push(latestComparisonFile);
          }
          break;
        }

        case ProposalArtifactType.MarketingSummary: {
          const latestMarketingReport = getLatestFileOfType(files, [FileType.MarketingReport]);
          if (latestMarketingReport) {
            selectedFiles.push(latestMarketingReport);
          }
          break;
        }

        default:
          break;
      }
    });

    return selectedFiles;
  }, [shouldAutoAttachFiles, proposalArtifacts, files]);

  const preSelectedFileIds = [...(submission?.proposal?.attachedFiles || []), ...autoSelectedFileIds];

  useLoadFilesSelection({
    setIsDirty,
    preSelectedFileIds,
  });
}
